import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios"

import { getMonitorPlatform } from "./monitor-context"
import { isCriticalHttpFailure } from "./sentry-filters"
import { scrubUrl } from "./sentry-privacy"
import { captureException, getSentry, isSentryEnabled } from "./sentry-state"

const META_KEY = "__monitorRequestMeta"

interface RequestMeta {
  startTime: number
}

function normalizeEndpoint(url: string | undefined) {
  return scrubUrl(url)?.replace(/\?.*$/, "") ?? "unknown"
}

function inferFeatureFromUrl(url: string | undefined) {
  if (!url) return "unknown"
  if (url.includes("payment")) return "payment"
  if (
    url.includes("promo") ||
    url.includes("voucher") ||
    url.includes("coupon")
  ) {
    return "promo"
  }
  if (
    url.includes("merchant") ||
    url.includes("marketplace") ||
    url.includes("food")
  ) {
    return "food"
  }
  if (url.includes("request_ride") || url.includes("be-autos")) return "ride"
  if (url.includes("identity") || url.includes("login")) return "auth"
  if (url.includes("configuration") || url.includes("home")) return "home"
  return "api"
}

export function instrumentHttpClient(
  client: AxiosInstance,
  options: { clientName: "appview" | "zalo-main" | "zalo-food" }
) {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const meta: RequestMeta = { startTime: performance.now() }
    ;(config as InternalAxiosRequestConfig & Record<string, unknown>)[
      META_KEY
    ] = meta

    return config
  })

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status ?? 0
      recordHttpFailure(error.config, status, options.clientName, error)
      return Promise.reject(error)
    }
  )
}

function recordHttpFailure(
  config: InternalAxiosRequestConfig | undefined,
  status: number,
  clientName: string,
  error: AxiosError
) {
  const meta = config
    ? ((config as InternalAxiosRequestConfig & Record<string, unknown>)[
        META_KEY
      ] as RequestMeta | undefined)
    : undefined
  const duration = meta ? performance.now() - meta.startTime : 0
  const endpoint = normalizeEndpoint(config?.url)
  const feature = inferFeatureFromUrl(config?.url)
  const platform = getMonitorPlatform()

  if (isSentryEnabled()) {
    getSentry()?.addBreadcrumb({
      category: "http",
      type: "http",
      level: status >= 500 || status === 0 ? "error" : "warning",
      data: {
        method: config?.method,
        url: endpoint,
        status,
        client: clientName,
      },
    })
  }

  if (!isCriticalHttpFailure(status, endpoint)) return

  captureException(error, {
    tags: {
      api_endpoint: endpoint,
      http_status: String(status),
      feature,
      client: clientName,
      platform,
    },
    extra: {
      duration_ms: Math.round(duration),
    },
    fingerprint: ["api-critical-failure", endpoint, String(status)],
  })
}
